"use client";

/**
 * AskShell (composer panels, spec 2026-09-19 §2): the ask card's markup, lifted
 * out of AskCard so the story and the composer render ONE chrome and can never
 * drift. The story's AskCard hands it the view-model's pieces and its controls;
 * a composer panel hands it a doc or a receipt. Presentational: no store, no
 * hooks, no verdicts. The rules are task-story.css's ts-need family, imported
 * here so every host that renders the shell loads them.
 *
 * `onClose` is the composer's ×; the story never passes it, so the story's DOM
 * is byte for byte what AskCard rendered before the lift (AskCard.lift.test).
 */
import type { ReactNode } from "react";
import { NEED_KIND_CSS_VAR, type NeedKind } from "@bash/shared";
import type { AskDetail } from "./askCopy";
import "./task-story.css";

/** The two panel kinds beside the nine ask kinds: the sheet wears Bash's amber, the receipt the done green. */
export type ShellKind = NeedKind | "sheet" | "receipt";
const PANEL_CC: Record<"sheet" | "receipt", string> = {
  sheet: "--hb-name",
  receipt: "--kind-green",
};

export function shellCssVar(kind: ShellKind): string {
  return kind === "sheet" || kind === "receipt"
    ? PANEL_CC[kind]
    : NEED_KIND_CSS_VAR[kind];
}

export interface AskShellProps {
  kind: ShellKind;
  /** The ask's identity (data-ask); the story passes the view's key, a panel none. */
  askKey?: string;
  /** What sits on the tile: the kind's glyph, a brand mark, the Chrome roundel. */
  tile: ReactNode;
  /** A brand or a page sits on the white plate (`is-brand`). */
  brandPlate?: boolean;
  title: string;
  why?: string;
  details?: AskDetail[];
  plan?: string;
  command?: string;
  /** The receipt line once a verdict was given: the card folds into it. */
  settled?: ReceiptText | null;
  error?: string | null;
  /** The composer's close; absent on the story, where a verdict folds the card. */
  onClose?: () => void;
  /** The controls (the story's verdicts), or a panel's body. */
  children?: ReactNode;
}

function Check() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** A receipt line's words: a string, or the file card's verb and object. */
export type ReceiptText = string | { verb: string; object: string };

/** The receipt line: "Allowed · Delete the build folder" reads with the
 *  verdict in ink and the ask quiet; a line without the dot is all verdict.
 *  Shared with the story's durable answered-question row, so a question you
 *  answered a week ago reads exactly like the one you answered just now.
 *  The file card's receipt reads the other way round (the approved mock,
 *  spec 2026-09-24): "Sent **clip.mov**", the verb quiet, the thing sent in ink. */
export function Receipt({ text }: { text: ReceiptText }) {
  const i = typeof text === "string" ? text.indexOf(" · ") : -1;
  return (
    <div className="ts-need-done">
      <i>
        <Check />
      </i>
      <span>
        {typeof text !== "string" ? (
          <>
            {text.verb} <b>{text.object}</b>
          </>
        ) : i === -1 ? (
          <b>{text}</b>
        ) : (
          <>
            <b>{text.slice(0, i)}</b>
            {text.slice(i)}
          </>
        )}
      </span>
    </div>
  );
}

export function AskShell({
  kind,
  askKey,
  tile,
  brandPlate = false,
  title,
  why,
  details,
  plan,
  command,
  settled,
  error,
  onClose,
  children,
}: AskShellProps) {
  return (
    <div
      className={`ts-need${settled ? " is-settled" : ""}`}
      data-kind={kind}
      data-ask={askKey}
      style={{ ["--cc" as string]: `var(${shellCssVar(kind)})` }}
    >
      {settled && <Receipt text={settled} />}
      <div className="ts-need-body" aria-hidden={settled ? true : undefined}>
        <div>
          <span className={`ts-need-tile${brandPlate ? " is-brand" : ""}`}>
            {tile}
          </span>
          {/* The × floats in the card's corner and reserves no room of its own,
              so a host that renders one gives the words a gutter to stop at:
              24 px clears the 18 px button with air and leaves the doc body
              its room on a 400 px crown. The story renders no × and keeps its
              column edge to edge. */}
          <div className={`ts-need-main${onClose ? " pr-6" : ""}`}>
            {/* No kind word (founder 09-02): the tile's colour says what kind
                of ask this is; the headline is the first thing read. */}
            <div className="ts-need-t">{title}</div>
            {why && <div className="ts-need-why">{why}</div>}
            {details && (
              <dl className="ts-need-dl">
                {details.map((d, i) => (
                  <div key={i} className="contents">
                    <dt>{d.label}</dt>
                    <dd className={d.tone ? `is-${d.tone}` : undefined}>
                      {d.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {plan && <div className="ts-need-plan">{plan}</div>}
            {command && (
              <details className="ts-need-more">
                <summary>Technical detail</summary>
                <pre>{command}</pre>
              </details>
            )}
            {children}
            {error && <div className="ts-need-err">{error}</div>}
          </div>
        </div>
      </div>
      {/* "Close panel", not "Close": the hosts that render one draw the shell
          INSIDE a card that has a Close of its own (the pill's thread, the
          inline card), so the two must read apart. */}
      {onClose && (
        <button
          type="button"
          className="ts-need-x"
          aria-label="Close panel"
          onClick={onClose}
        >
          ×
        </button>
      )}
    </div>
  );
}
